import { ChangeEvent, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateBlogMutation, useLazyGetBlogByIdQuery, useUpdateBlogMutation } from '@modules/blog';
import { MESSAGES } from '@app/constant';
import { useCreateImageMutation } from '@modules/images';
import { useParams } from 'react-router-dom';
import { MultipleContent } from '@modules/blog/types';

import { createBlogSchema, initialFormState } from './data';
import { onChangeUpload } from './localHelper';
import { ICreateBlogFormData } from './types';
import { prepareClonedData, prepareImageUploadPromises } from './utils';
import { blogFirstKey } from './constant';

export interface IFlowProcess {
  isLoading: boolean;
  isSuccess: string;
  largeImageMessage: string;
  isImageViewOpen: boolean;
  isButtonLoading: boolean;
  imageSrcForAction: string;
  isDeleteModalOpen: boolean;
}

const initialFlowState = {
  isLoading: true,
  isSuccess: '',
  largeImageMessage: '',
  isImageViewOpen: false,
  isButtonLoading: false,
  imageSrcForAction: '',
  isDeleteModalOpen: false,
}

export const useCreateBlogLogic = () => {
  const { id } = useParams()
  const [createBlog] = useCreateBlogMutation()
  const [updateBlog] = useUpdateBlogMutation()
  const [createImage] = useCreateImageMutation()
  const [getBlogById] = useLazyGetBlogByIdQuery();
  const [flowProcess, setFlowProcess] = useState<IFlowProcess>(initialFlowState)

  const methods = useForm<ICreateBlogFormData>({
    // @ts-ignore
    resolver: yupResolver(createBlogSchema),
    mode: 'onSubmit',
    defaultValues: initialFormState
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = methods;

  const blogContent = watch(blogFirstKey)

  const handleLoadingState = (isLoading: boolean, successMessage: string = '') => {
    setFlowProcess((prev) => ({
      ...prev,
      isButtonLoading: isLoading,
      isSuccess: successMessage,
      largeImageMessage: '',
    }));
  };

  const onSubmit = async (data: ICreateBlogFormData) => {
    handleLoadingState(true);

    try {
      const imageUploadPromises = prepareImageUploadPromises(data, createImage);

      const uploadedImages = await Promise.all(imageUploadPromises);
      const imagePaths = uploadedImages?.map((result) => result?.id || '');

      const clonedData = prepareClonedData(data, imagePaths, id);

      if (id) {
        await updateBlog({ id, body: clonedData }).unwrap()
      } else {
        await createBlog(clonedData).unwrap();
        reset(initialFormState);
      }

      handleLoadingState(false, id ? MESSAGES.blogHasBeenSuccessfullyUpdated : MESSAGES.blogHasBeenSuccessfullyCreated);
    } catch (error: any) {
      handleLoadingState(false, error?.data?.message || MESSAGES.somethingWentWrong);
    }
  };

  const getBlogData = async () => {
    if (!id) {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))

      return
    }

    try {
      const blog = await getBlogById(id).unwrap()

      const content = JSON.parse(JSON.stringify(blog.content))

      const blogWithSortedContent = {
        ...blog,
        content: content.sort((a: MultipleContent, b: MultipleContent) => a.sort - b.sort)
      }

      reset(blogWithSortedContent);
    } catch (error) {
      console.log("Error", error);
    } finally {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))
    }
  }

  useEffect(() => {
    getBlogData()
  }, [id])

  return {
    ...flowProcess,
    paramId: id,
    methods,
    errors,
    blogContent,
    watch,
    setValue,
    setFlowProcess,
    onSubmit,
    register,
    handleSubmit,
    // update here
    onChangeUpload: (
      event: ChangeEvent<HTMLInputElement>,
      index: number
    ) => onChangeUpload({
      index,
      event,
      watch,
      setValue,
      setState: setFlowProcess,
      setError,
    }),
  }
}
